import type { Notification } from "@storytell/client";
import { urls } from "@storytell/core";
import { type RoleName, RoleNameSchema } from "@storytell/types";
import { roleLabelKey } from "~/components/organization/roleLabels";
import type { TranslationKey } from "~/i18n/translations";

// Type is code-owned on the backend and the client union is open, so an old
// client can receive a type it doesn't know. Known types map to i18n keys +
// params here; unknown types render the stored English fallbackTitle/Body.
export interface NotificationDisplay {
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  // A role label is itself translated, so the caller resolves it and passes the
  // result back in as the `role` param.
  roleKey?: TranslationKey;
  params?: Record<string, string | number>;
}

function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const v = payload[key];
  return typeof v === "string" ? v : undefined;
}

// Organization payloads carry the bare role name; everything else in the app
// keys off the namespaced form.
function readRole(payload: Record<string, unknown>): RoleName | undefined {
  const parsed = RoleNameSchema.safeParse(`role.${readString(payload, "role")}`);
  return parsed.success ? parsed.data : undefined;
}

export function getNotificationDisplay(n: Notification): NotificationDisplay | null {
  switch (n.type) {
    case "billing.payment_failed":
      return {
        titleKey: "notifications.type.billing_payment_failed.title",
        bodyKey: "notifications.type.billing_payment_failed.body",
      };
    case "billing.subscription.trial_ending":
      return {
        titleKey: "notifications.type.billing_subscription_trial_ending.title",
        bodyKey: "notifications.type.billing_subscription_trial_ending.body",
      };
    // Custom plans are staff-managed, so a cancel from Stripe's side sends the
    // owner to support instead of showing the self-serve cancel copy.
    case "billing.subscription.canceled":
      return readString(n.payload, "code") === "custom_plan_billing_stopped"
        ? {
            titleKey: "notifications.type.billing_subscription_canceled.title_custom_plan_billing_stopped",
            bodyKey: "notifications.type.billing_subscription_canceled.body_custom_plan_billing_stopped",
          }
        : {
            titleKey: "notifications.type.billing_subscription_canceled.title",
            bodyKey: "notifications.type.billing_subscription_canceled.body",
          };
    case "billing.subscription.downgraded":
      return {
        titleKey: "notifications.type.billing_subscription_downgraded.title",
        bodyKey: "notifications.type.billing_subscription_downgraded.body",
      };
    case "billing.subscription.expired":
      return {
        titleKey: "notifications.type.billing_subscription_expired.title",
        bodyKey: "notifications.type.billing_subscription_expired.body",
      };
    case "billing.credits.threshold": {
      const raw = n.payload.percent;
      const pct = typeof raw === "number" ? raw : Number(raw ?? 0);
      return {
        titleKey: "notifications.type.billing_credits_threshold.title",
        bodyKey: "notifications.type.billing_credits_threshold.body",
        params: { pct },
      };
    }
    // The payload's reason is English written by the server, so the body is
    // picked from the code instead. Only payer_not_owner goes to the remaining
    // owners rather than the payer, so it alone asks for a different fix.
    case "billing.auto_topup.failed":
      return {
        titleKey: "notifications.type.billing_auto_topup_failed.title",
        bodyKey:
          readString(n.payload, "code") === "payer_not_owner"
            ? "notifications.type.billing_auto_topup_failed.body_payer_not_owner"
            : "notifications.type.billing_auto_topup_failed.body",
      };
    case "billing.auto_topup.cap_reached":
      return {
        titleKey: "notifications.type.billing_auto_topup_cap_reached.title",
        bodyKey: "notifications.type.billing_auto_topup_cap_reached.body",
      };
    case "activity.prompt.completed": {
      const title = readString(n.payload, "chatTitle");
      return {
        titleKey: "notifications.type.activity_prompt_completed.title",
        bodyKey: title
          ? "notifications.type.activity_prompt_completed.body_titled"
          : "notifications.type.activity_prompt_completed.body",
        ...(title ? { params: { title } } : {}),
      };
    }
    case "activity.prompt.failed": {
      const title = readString(n.payload, "chatTitle");
      return {
        titleKey: "notifications.type.activity_prompt_failed.title",
        bodyKey: title
          ? "notifications.type.activity_prompt_failed.body_titled"
          : "notifications.type.activity_prompt_failed.body",
        ...(title ? { params: { title } } : {}),
      };
    }
    case "actions.transferred": {
      const name = readString(n.payload, "actionName");
      return {
        titleKey: "notifications.type.actions_transferred.title",
        bodyKey: name
          ? "notifications.type.actions_transferred.body_named"
          : "notifications.type.actions_transferred.body",
        ...(name ? { params: { name } } : {}),
      };
    }
    case "session.invite": {
      const inviter = readString(n.payload, "invitedBy");
      const session = readString(n.payload, "sessionName");
      if (inviter && session) {
        return {
          titleKey: "notifications.type.session_invite.title",
          bodyKey: "notifications.type.session_invite.body_named",
          params: { inviter, session },
        };
      }
      return {
        titleKey: "notifications.type.session_invite.title",
        bodyKey: "notifications.type.session_invite.body",
      };
    }
    case "session.live": {
      const host = readString(n.payload, "hostName");
      const session = readString(n.payload, "sessionName");
      return {
        titleKey: host ? "notifications.type.session_live.title_named" : "notifications.type.session_live.title",
        bodyKey: session ? "notifications.type.session_live.body_named" : "notifications.type.session_live.body",
        ...(host || session ? { params: { ...(host ? { host } : {}), ...(session ? { session } : {}) } } : {}),
      };
    }
    case "session.summary_ready": {
      const session = readString(n.payload, "sessionName");
      return {
        titleKey: "notifications.type.session_summary_ready.title",
        bodyKey: session
          ? "notifications.type.session_summary_ready.body_named"
          : "notifications.type.session_summary_ready.body",
        ...(session ? { params: { session } } : {}),
      };
    }
    case "org.invitation.received": {
      // These cases interpolate the actor and the role, so a payload missing
      // either drops to the server copy rather than a half-substituted sentence.
      const role = readRole(n.payload);
      const inviter = readString(n.payload, "invitedBy");
      if (!role || !inviter) return null;
      const org = readString(n.payload, "organizationName");
      return {
        titleKey: "notifications.type.org_invitation_received.title",
        bodyKey: org
          ? "notifications.type.org_invitation_received.body_named"
          : "notifications.type.org_invitation_received.body",
        roleKey: roleLabelKey(role),
        params: { inviter, ...(org ? { org } : {}) },
      };
    }
    case "org.invitation.accepted": {
      const role = readRole(n.payload);
      const invitee = readString(n.payload, "acceptedBy");
      if (!role || !invitee) return null;
      const org = readString(n.payload, "organizationName");
      return {
        titleKey: "notifications.type.org_invitation_accepted.title",
        bodyKey: org
          ? "notifications.type.org_invitation_accepted.body_named"
          : "notifications.type.org_invitation_accepted.body",
        roleKey: roleLabelKey(role),
        params: { invitee, ...(org ? { org } : {}) },
      };
    }
    case "org.role.changed": {
      const role = readRole(n.payload);
      if (!role) return null;
      const org = readString(n.payload, "organizationName");
      return {
        titleKey: "notifications.type.org_role_changed.title",
        bodyKey: org ? "notifications.type.org_role_changed.body_named" : "notifications.type.org_role_changed.body",
        roleKey: roleLabelKey(role),
        ...(org ? { params: { org } } : {}),
      };
    }
    case "org.member.removed": {
      const org = readString(n.payload, "organizationName");
      return {
        titleKey: "notifications.type.org_member_removed.title",
        bodyKey: org
          ? "notifications.type.org_member_removed.body_named"
          : "notifications.type.org_member_removed.body",
        ...(org ? { params: { org } } : {}),
      };
    }
    case "integrations.slack.update_needed": {
      const app = readString(n.payload, "appName");
      return {
        titleKey: "notifications.type.integrations_slack_update_needed.title",
        bodyKey: app
          ? "notifications.type.integrations_slack_update_needed.body_named"
          : "notifications.type.integrations_slack_update_needed.body",
        ...(app ? { params: { app } } : {}),
      };
    }
    default:
      return null;
  }
}

// Prompt-activity notifications open the originating chat, scoped to its
// project so the project-strict chat fetch resolves.
export function getNotificationHref(n: Notification): string {
  // Billing lives on the org page; a notification without an org id falls back
  // to the legacy settings URL, which redirects to the owned org's billing.
  if (n.type.startsWith("billing.")) {
    return n.organizationId ? `/orgs/${n.organizationId}/billing` : "/settings/billing";
  }

  // The invitation's own accept link is issued against the auth service, which
  // the client-side router can't follow. The recipient is already signed in, so
  // this links straight at the route that resolver would have landed them on.
  if (n.type === "org.invitation.received") {
    const invitationId = readString(n.payload, "invitationId");
    return invitationId ? `/invites?invite=${encodeURIComponent(invitationId)}` : "/";
  }

  // Only owners can invite, so the recipient of an acceptance can reach the
  // owner-only members list.
  if (n.type === "org.invitation.accepted") {
    return n.organizationId ? `/orgs/${n.organizationId}/members` : "/";
  }

  if (n.type === "org.role.changed") {
    return n.organizationId ? `/orgs/${n.organizationId}` : "/";
  }

  if (n.type === "org.member.removed") return "/";

  if (n.type === "integrations.slack.update_needed") {
    return n.organizationId ? `/orgs/${n.organizationId}/integrations` : "/";
  }

  if (n.type === "actions.transferred") {
    const projectId = readString(n.payload, "projectId");
    const actionId = readString(n.payload, "actionId");
    return projectId && actionId ? `/project/${projectId}/actions/${actionId}` : "/";
  }

  if (n.type === "session.invite" || n.type === "session.live" || n.type === "session.summary_ready") {
    const projectId = readString(n.payload, "projectId");
    const sessionId = readString(n.payload, "sessionId");
    return projectId && sessionId ? `/project/${projectId}/live-sessions/${sessionId}` : "/";
  }

  const projectId = readString(n.payload, "projectId");
  const chatId = readString(n.payload, "chatId");
  const messageId = readString(n.payload, "messageId");

  if (!chatId) return "/";
  return urls.chatHref(projectId, chatId, messageId);
}

// An activity toast is noise when its chat is already on screen — the update
// still lands in the panel list and the badge. Billing toasts always surface.
export function shouldSuppressToast(n: Notification, pathname: string): boolean {
  if (!n.type.startsWith("activity.")) return false;
  const chatId = readString(n.payload, "chatId");
  return chatId !== undefined && pathname.includes(chatId);
}
